import { Card, Col, Row, Avatar, Typography, Descriptions, Image, Drawer, Space, Button, Tag, Divider, Empty } from "antd";
import { UserOutlined, CarOutlined, PhoneOutlined, MailOutlined, IdcardOutlined, HomeOutlined, EnvironmentOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useContext, useMemo } from "react";
import { DriverContext } from ".";

const { Title, Text } = Typography;

const ViewDrivers = () => {

    const { viewModalOpen, setViewModalOpen, driver } = useContext(DriverContext);

    const fullName = useMemo(() => {
        const fn = driver?.firstName || "";
        const ln = driver?.lastName || "";
        const name = `${fn} ${ln}`.trim();
        return name || "Driver";
    }, [driver?.firstName, driver?.lastName]);

    const initials = useMemo(() => {
        const parts = String(fullName || "")
            .split(" ")
            .map((p) => p.trim())
            .filter(Boolean);
        return (parts[0]?.[0] || "D").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
    }, [fullName]);

    const docs = useMemo(() => ([
        {
            key: "drivingLicense",
            title: "Driving License",
            icon: <IdcardOutlined style={{ color: "#1677ff" }} />,
            // Backend uses `documentsData` for driving license in this UI flow
            data: driver?.documentsData || null,
        },
        {
            key: "identityProof",
            title: "Identity Proof",
            icon: <IdcardOutlined style={{ color: "#52c41a" }} />,
            // Backend uses `identityDocumentsData` for identity docs in this UI flow
            data: driver?.identityDocumentsData || null,
        },
    ]), [driver?.documentsData, driver?.identityDocumentsData]);

    const warehouseNames = useMemo(() => {
        if (Array.isArray(driver?.warehouses)) {
            return driver.warehouses
                .map((w) => (typeof w === "string" ? w : w?.name))
                .filter(Boolean);
        }
        return [];
    }, [driver?.warehouses]);

    const renderDocCard = (d) => {
        const url = d?.data?.url;
        return (
            <Card
                key={d.key}
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", width: "100%" }}>
                        <Space size={8}>
                            {d.icon}
                            <span style={{ fontWeight: 700 }}>{d.title}</span>
                        </Space>
                        {url ? (
                            <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                >
                                    View
                                </Button>
                                <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                >
                                    Download
                                </Button>
                            </div>
                        ) : null}
                    </div>
                }
                bordered={false}
                style={{
                    height: "100%",
                    border: "1px solid #f0f0f0",
                    borderRadius: 12,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}
            >
                {url ? (
                    <Image
                        width="100%"
                        src={url}
                        height={220}
                        style={{ borderRadius: 10, objectFit: "contain" }}
                        preview={{ mask: "Preview" }}
                    />
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<Text type="secondary">No document uploaded</Text>}
                    />
                )}
            </Card>
        );
    };

    return (
        <Drawer
            title={
                <Space size={10} style={{ minWidth: 0 }}>
                    <Avatar
                        size={36}
                        src={driver?.photoData?.url || null}
                        icon={<UserOutlined />}
                        style={{ background: "#1677ff", flexShrink: 0 }}
                    >
                        {initials}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {fullName}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            View Driver Details
                        </Text>
                    </div>
                </Space>
            }
            width={720}
            onClose={() => setViewModalOpen(false)}
            open={viewModalOpen}
            styles={{ body: { paddingBottom: 80 } }}
            extra={
                <Space>
                    <Button onClick={() => setViewModalOpen(false)}>Cancel</Button>
                </Space>
            }
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Profile header */}
                <Card
                    bordered={false}
                    style={{
                        border: "1px solid #f0f0f0",
                        borderRadius: 12,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={10}>
                            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                <Avatar
                                    size={84}
                                    src={driver?.photoData?.url || null}
                                    icon={<UserOutlined />}
                                    style={{ background: "#1677ff", fontWeight: 800 }}
                                >
                                    {initials}
                                </Avatar>
                                <div style={{ minWidth: 0 }}>
                                    <Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
                                        {fullName}
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                                        {driver?.email || "—"}
                                    </Text>
                                    <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        <Tag color={driver?.isAvailable === "Online" ? "green" : "default"}>
                                            {driver?.isAvailable || "Offline"}
                                        </Tag>
                                        {driver?.vehicleType && (
                                            <Tag icon={<CarOutlined />} color="blue">
                                                {driver.vehicleType}
                                            </Tag>
                                        )}
                                        {warehouseNames.length > 0 && (
                                            <Tag icon={<HomeOutlined />} color="purple">
                                                {warehouseNames.length} Warehouse{warehouseNames.length === 1 ? "" : "s"}
                                            </Tag>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col xs={24} sm={14}>
                            <Descriptions
                                size="small"
                                column={1}
                                styles={{ label: { width: 140 } }}
                            >
                                <Descriptions.Item
                                    label={<Space size={6}><UserOutlined />User Name</Space>}
                                >
                                    {driver?.userName || "—"}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={<Space size={6}><PhoneOutlined />Phone</Space>}
                                >
                                    {driver?.phone || "—"}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={<Space size={6}><MailOutlined />Email</Space>}
                                >
                                    {driver?.email || "—"}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={<Space size={6}><CarOutlined />Vehicle</Space>}
                                >
                                    {(driver?.vehicleType || driver?.vehicle_number)
                                        ? `${driver?.vehicleType || "—"}${driver?.vehicle_number ? ` (${driver.vehicle_number})` : ""}`
                                        : "—"}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={<Space size={6}><EnvironmentOutlined />Address</Space>}
                                >
                                    {[
                                        driver?.address,
                                        driver?.city,
                                        driver?.state,
                                        driver?.zip_code ? `- ${driver.zip_code}` : null,
                                    ].filter(Boolean).join(", ") || "—"}
                                </Descriptions.Item>
                            </Descriptions>
                        </Col>
                    </Row>

                    {warehouseNames.length > 0 && (
                        <>
                            <Divider style={{ margin: "12px 0" }} />
                            <div>
                                <Text strong style={{ display: "block", marginBottom: 6 }}>
                                    Assigned Warehouses
                                </Text>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {warehouseNames.map((n) => (
                                        <Tag key={n} color="geekblue">
                                            {n}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </Card>

                {/* Documents */}
                <Row gutter={[12, 12]}>
                    {docs.map((d) => (
                        <Col xs={24} md={12} key={d.key}>
                            {renderDocCard(d)}
                        </Col>
                    ))}
                </Row>
            </div>
        </Drawer>
    )
}
export default ViewDrivers